import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import NavPane from './components/NavPane';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/settings/SettingsPage';
import AccountPage from './pages/AccountPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PatientsPage from './pages/PatientsPage';
import PatientChartPage from './pages/PatientChartPage';
import ForcePasswordChangePage from './pages/ForcePasswordChangePage';
import EnrollMFAPage from './pages/EnrollMFAPage';
import MFARecoveryPage from './pages/MFARecoveryPage';
import TopBar from './components/TopBar';

const NAV_WIDTH_COLLAPSED = '60px';
const NAV_WIDTH_EXPANDED = '176px';


/* ----------------------------------------------------------
   App shell layout — receives expanded state from App
---------------------------------------------------------- */
function AppLayout({ children, expanded, setExpanded }) {
  const navWidth = expanded ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED;

  return (
    <div style={styles.appShell}>
      <NavPane expanded={expanded} setExpanded={setExpanded} />
      <div style={{
        ...styles.rightSide,
        marginLeft: navWidth,
        transition: 'margin-left 0.2s ease',
      }}>
        <TopBar providerFilter={null} />
        <main style={styles.mainContent}>
          {children}
        </main>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------
   Placeholder pages for future stages
---------------------------------------------------------- */
function PlaceholderPage({ title, stage }) {
  return (
    <div style={styles.placeholder}>
      <div style={styles.placeholderCard}>
        <h2 style={styles.placeholderTitle}>{title}</h2>
        <p style={styles.placeholderText}>Coming in {stage}</p>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------
   App root — owns nav expanded state
---------------------------------------------------------- */
export default function App() {
  const [navExpanded, setNavExpanded] = useState(false);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Auth flow routes */}
          <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
          <Route path="/enroll-mfa" element={<EnrollMFAPage />} />
          <Route path="/mfa-recovery" element={<MFARecoveryPage />} />

          {/* Protected app routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute><AppLayout expanded={navExpanded} setExpanded={setNavExpanded}>
              <DashboardPage />
            </AppLayout></ProtectedRoute>
          } />
          <Route path="/patients" element={
            <ProtectedRoute><AppLayout expanded={navExpanded} setExpanded={setNavExpanded}>
              <PatientsPage />
            </AppLayout></ProtectedRoute>
          } />
          <Route path="/patients/:patientId" element={
            <ProtectedRoute><AppLayout expanded={navExpanded} setExpanded={setNavExpanded}>
              <PatientChartPage />
            </AppLayout></ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute><AppLayout expanded={navExpanded} setExpanded={setNavExpanded}>
              <SettingsPage />
            </AppLayout></ProtectedRoute>
          } />
          <Route path="/account" element={
            <ProtectedRoute><AppLayout expanded={navExpanded} setExpanded={setNavExpanded}>
              <AccountPage />
            </AppLayout></ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute><AppLayout expanded={navExpanded} setExpanded={setNavExpanded}>
              <PlaceholderPage title="Report Center" stage="Stage 14" />
            </AppLayout></ProtectedRoute>
          } />
          <Route path="/referrals" element={
            <ProtectedRoute><AppLayout expanded={navExpanded} setExpanded={setNavExpanded}>
              <PlaceholderPage title="Referral Hub" stage="Stage 12–13" />
            </AppLayout></ProtectedRoute>
          } />
          <Route path="/resources" element={
            <ProtectedRoute><AppLayout expanded={navExpanded} setExpanded={setNavExpanded}>
              <PlaceholderPage title="Resource Center" stage="Stage 15" />
            </AppLayout></ProtectedRoute>
          } />
          <Route path="/community" element={
            <ProtectedRoute><AppLayout expanded={navExpanded} setExpanded={setNavExpanded}>
              <PlaceholderPage title="Nephros Community" stage="Phase 2" />
            </AppLayout></ProtectedRoute>
          } />

          {/* Fallback */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

/* ----------------------------------------------------------
   Styles
---------------------------------------------------------- */
const styles = {
  appShell: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: 'var(--color-bg-page)',
  },
  rightSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    minWidth: 0,
  },
  topBar: {
    height: '48px',
    backgroundColor: 'var(--color-bg-card)',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 var(--space-4)',
    flexShrink: 0,
  },
  searchForm: {
    width: '300px',
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    color: 'var(--color-text-muted)',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    paddingLeft: '34px',
    paddingRight: '12px',
    paddingTop: '7px',
    paddingBottom: '7px',
    fontSize: 'var(--font-size-sm)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-bg-page)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'var(--font-family-base)',
  },
  mainContent: {
    flex: 1,
    padding: '16px',
    boxSizing: 'border-box',
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
  },
  placeholderCard: {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    padding: '48px 64px',
    textAlign: 'center',
  },
  placeholderTitle: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: '0 0 8px',
    fontFamily: 'var(--font-family-base)',
  },
  placeholderText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-muted)',
    margin: 0,
    fontFamily: 'var(--font-family-base)',
  },
};